import { Action, getModule, Module, Mutation, MutationAction } from "vuex-module-decorators"
import store from "@/store"

import VuexModuleLoading from "@/store/VuexModuleLoading"
import TemplateSubmissionService from "@/services/submission/templateSubmission/TemplateSubmissionService"
import { TemplateSubmission } from "@/model/interfaces/submission/templateSubmission/templateSubmission"
import CanService from "@/services/submission/templateSubmission/CanService"
import { CanContent } from "@/model/interfaces/submission/templateSubmission/CanContent"
import axios from "axios"
import TemplateSubmissionModule from "@/store/modules/submission/TemplateSubmissionModule"

@Module({ dynamic: true, store, name: "CanModule", namespaced: true })
class CanModule extends VuexModuleLoading {
  canContent: CanContent | null = null
  canContentHistoryPile: CanContent["html"][] = []
  activeCanContentHistoryItemKey: number | null = null
  canError: string | null = null

  @Action
  async setCanContent(payload: { submission: TemplateSubmission; reset: boolean }) {
    if (payload.reset) {
      await TemplateSubmissionService.resetCanContent(payload.submission, TemplateSubmissionModule.canViewSelected)
        .then(canContent => {
          getModule(CanModule).setContentHistoryPile([])
          getModule(CanModule).setActiveCanContentHistoryItem(null)
          getModule(CanModule).setCanContentItem(canContent)
        })
        .catch(error => {
          if (error && axios.isAxiosError(error) && error.response?.data?.["hydra:description"]) {
            this.canError = error.response.data["hydra:description"]
          } else {
            this.canError = "An error occurred while resetting CAN content."
          }
        })
    } else {
      getModule(CanModule).setContentHistoryPile([])
      getModule(CanModule).setActiveCanContentHistoryItem(null)
      const canContent = await TemplateSubmissionService.getCanContent(payload.submission, TemplateSubmissionModule.canViewSelected)
      getModule(CanModule).setCanContentItem(canContent)
    }

    getModule(CanModule).eraseNextCanContentHistoryItem()
    if (this.canContent) {
      getModule(CanModule).pushCanContentHistoryPile(this.canContent.html)
    }
  }

  @Mutation
  setCanContentItem(canContent: CanContent | null) {
    this.canContent = canContent
  }

  @Mutation
  setContentHistoryPile(canContentHistoryPile: CanContent["html"][]) {
    this.canContentHistoryPile = canContentHistoryPile
  }

  @Mutation
  setActiveCanContentHistoryItem(activeCanContentHistoryItemKey: number | null) {
    this.activeCanContentHistoryItemKey = activeCanContentHistoryItemKey
  }

  @Mutation
  setCanError(error: string | null) {
    this.canError = error
  }

  @Mutation
  pushCanContentHistoryPile(canContentHtml: CanContent["html"]) {
    this.canContentHistoryPile.push(canContentHtml)
    getModule(CanModule).setActiveCanContentHistoryItemKey()
  }

  @Mutation
  setActiveCanContentHistoryItemKey(newKey: number | null = null) {
    const key = newKey ?? (this.canContentHistoryPile.length > 0 ? this.canContentHistoryPile.length - 1 : null)
    if (key != null && this.canContentHistoryPile[key] !== undefined) {
      this.activeCanContentHistoryItemKey = key
    }
  }

  @Mutation
  eraseNextCanContentHistoryItem() {
    if (this.activeCanContentHistoryItemKey) {
      this.canContentHistoryPile.length = this.activeCanContentHistoryItemKey + 1
    }
  }

  @Action
  prevCanContentHistoryItem() {
    if (this.activeCanContentHistoryItemKey != null) {
      const prevKey = this.activeCanContentHistoryItemKey - 1
      this.context.commit("setActiveCanContentHistoryItemKey", prevKey)
    }
  }

  @Action
  nextCanContentHistoryItem() {
    if (this.activeCanContentHistoryItemKey != null) {
      const nextKey = this.activeCanContentHistoryItemKey + 1
      this.context.commit("setActiveCanContentHistoryItemKey", nextKey)
    }
  }

  @Mutation
  update(canContent: Partial<CanContent>) {
    if (this.canContent?.id === canContent.id) {
      this.canContent = { ...this.canContent, ...canContent } as CanContent
    }
  }

  @Mutation
  updateLocked(canContent: Pick<CanContent, "id" | "locked"> & Partial<CanContent>) {
    if (this.canContent && this.canContent?.id === canContent.id && this.canContent?.locked !== canContent.locked) {
      this.canContent.locked = canContent.locked
    }
  }

  @MutationAction({ mutate: ["canContent"] })
  async saveCanContent(canContent: Partial<CanContent>) {
    const patchedCanContent: CanContent = await CanService.patch(canContent)
    return { canContent: { ...this.canContent, ...patchedCanContent } }
  }

  get activeCanContent(): CanContent["html"] | null {
    if (this.activeCanContentHistoryItemKey != null) {
      return this.canContentHistoryPile[this.activeCanContentHistoryItemKey]
    }
    return null
  }

  get hasPrevCanContentItem(): boolean {
    return this.activeCanContentHistoryItemKey != null ? this.activeCanContentHistoryItemKey > 0 : false
  }

  get hasNextCanContentItem(): boolean {
    return this.activeCanContentHistoryItemKey != null ? this.activeCanContentHistoryItemKey < this.canContentHistoryPile.length - 1 : false
  }
}

export default getModule(CanModule)
